import { useState, type FormEvent } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useRessource } from '../hooks/useRessource';
import { ErreurApi, api } from '../lib/apiClient';
import {
  Badge,
  Champ,
  Chargement,
  EnTetePage,
  Erreur,
  Modale,
  Tableau,
  type Colonne,
} from '../components/Elements';
import { PiedFormulaire } from './ReferentielsPages';
import { BandeauAnneeFigee } from '../components/SelecteurAnnee';
import { useAnnee } from '../hooks/useAnnee';
import { enCentimes, enEuros, formaterDuree, formaterMontant } from '../lib/format';
import type { FamilleActivite, SuiviPresence, TypeActivite } from '../types';

const FAMILLES: FamilleActivite[] = ['Instruments', 'Chant', 'Chorale', 'Eveil', 'Handicap'];

/**
 * CDC M1 : gestion du référentiel tarifaire.
 *
 * Le type d'activité portait auparavant cinq valeurs figées. Elles ne
 * pouvaient pas porter les tarifs : le solfège est de famille
 * « Instruments » et coûte 300 €, quand l'instrument en coûte 950. Le
 * référentiel a donc la granularité de la grille réelle de l'école, et
 * conserve les cinq familles pour les ventilations du tableau de bord.
 */
export function TypesActivitePage() {
  const { peut } = useAuth();
  const [inclureInactifs, setInclureInactifs] = useState(false);
  const [modale, setModale] = useState<{ ouverte: boolean; type: TypeActivite | null }>({
    ouverte: false,
    type: null,
  });

  // Referentiel ANNUEL : l'evolution des tarifs se lit en changeant
  // d'annee. Une annee cloturee est figee, tarifs compris (RG-27).
  const annee = useAnnee();
  const peutGerer = peut('type_activite.gerer') && annee.modifiable;

  const types = useRessource(
    () =>
      annee.anneeId
        ? api.get<TypeActivite[]>('/api/types-activite', { inclureInactifs, anneeId: annee.anneeId })
        : Promise.resolve([]),
    [inclureInactifs, annee.anneeId],
  );

  const orphelines = useRessource(
    () => api.get<{ id: string; libelle: string; type: string }[]>('/api/types-activite/orphelines'),
    [],
  );

  // L'ORDRE SE POSE EN GLISSANT LES LIGNES (2026-10-12), comme pour les
  // types de reduction : l'ordre local s'applique aussitot, le serveur
  // l'enregistre, et les ACTIVITES suivent ce rang puis l'alphabet.
  const [ordreLocal, setOrdreLocal] = useState<string[] | null>(null);
  const lignes = (() => {
    const donnees = types.donnees ?? [];
    if (!ordreLocal) return donnees;
    const rang = new Map(ordreLocal.map((id, index) => [id, index]));
    return [...donnees].sort(
      (a, b) => (rang.get(a.id) ?? Number.MAX_SAFE_INTEGER) - (rang.get(b.id) ?? Number.MAX_SAFE_INTEGER),
    );
  })();

  const reordonner = async (ids: string[]) => {
    setOrdreLocal(ids);
    try {
      await api.put('/api/types-activite/ordre', { anneeId: annee.anneeId, ids });
      types.recharger();
    } catch (cause) {
      setOrdreLocal(null);
      window.alert(cause instanceof ErreurApi ? cause.message : 'Réordonnancement impossible.');
    }
  };

  const basculer = async (type: TypeActivite) => {
    try {
      await api.patch(`/api/types-activite/${type.id}/activation`, { actif: !type.actif });
      types.recharger();
    } catch (cause) {
      window.alert(cause instanceof ErreurApi ? cause.message : 'Opération impossible.');
    }
  };

  const colonnes: Colonne<TypeActivite>[] = [
    {
      cle: 'libelle',
      entete: 'Type d’activité',
      rendu: (t) => (
        <div>
          <div className="font-medium">{t.libelle}</div>
          <div className="font-mono text-xs text-encre-400">{t.code}</div>
        </div>
      ),
    },
    { cle: 'famille', entete: 'Famille', rendu: (t) => <Badge>{t.famille}</Badge> },
    // Individuel ou groupe : declare sur le type (2026-09-30), la
    // capacite ne le dit pas — deux eleves en alternance partagent un
    // creneau de deux places et suivent un cours individuel.
    {
      cle: 'nature',
      entete: 'Cours',
      rendu: (t) =>
        t.individuel ? <Badge ton="contour">indiv.</Badge> : <Badge>groupe</Badge>,
    },
    {
      cle: 'tarif',
      entete: 'Tarif annuel',
      classe: 'text-right',
      rendu: (t) => <span className="tabulaire font-medium">{formaterMontant(t.tarif)}</span>,
    },
    {
      cle: 'duree',
      entete: 'Durée',
      classe: 'text-right',
      rendu: (t) => <span className="tabulaire">{formaterDuree(t.duree_minutes)}</span>,
    },
    {
      cle: 'capacite',
      entete: 'Capacité',
      classe: 'text-right',
      rendu: (t) => (
        <span className="tabulaire">
          {t.capacite_max === null ? '—' : `${t.capacite_max} pl.`}
          {t.tolerance_pourcent > 0 && (
            <span className="ml-1 text-xs text-encre-400">+{t.tolerance_pourcent} %</span>
          )}
        </span>
      ),
    },
    {
      cle: 'activites',
      entete: 'Activités',
      classe: 'text-right',
      rendu: (t) => <span className="tabulaire">{t.nb_activites ?? 0}</span>,
    },
    {
      cle: 'commentaire',
      entete: 'Conditions',
      rendu: (t) => <span className="text-xs text-encre-500">{t.commentaire ?? '—'}</span>,
    },
    {
      cle: 'statut',
      entete: 'Statut',
      rendu: (t) => (
        <Badge ton={t.actif ? 'succes' : 'neutre'}>{t.actif ? 'Actif' : 'Désactivé'}</Badge>
      ),
    },
    {
      cle: 'actions',
      entete: '',
      classe: 'text-right',
      rendu: (t) =>
        peutGerer ? (
          <button
            type="button"
            className="text-sm text-encre-500 underline hover:text-encre-900"
            onClick={(evenement) => {
              evenement.stopPropagation();
              void basculer(t);
            }}
          >
            {t.actif ? 'Désactiver' : 'Réactiver'}
          </button>
        ) : null,
    },
  ];

  return (
    <>
      <EnTetePage
        titre="Types d’activité"
        description="Le tarif annuel se fixe ici, par type. Il pré-remplit la saisie d’une inscription — il ne l’impose pas : prorata en cours d’année, réduction fratrie et cas particuliers s’en écartent légitimement. L’ordre de cette liste classe les activités partout : glissez les lignes pour le changer."
        actions={
          <span className="flex items-center gap-3">
            {peutGerer && (
              <button
                type="button"
                className="bouton-principal"
                onClick={() => setModale({ ouverte: true, type: null })}
              >
                Nouveau type
              </button>
            )}
          </span>
        }
      />

      {annee.annee && !annee.modifiable && <BandeauAnneeFigee annee={annee.annee} />}

      {/* Une activité sans type n'a pas de tarif de référence : son montant
          reste entièrement à saisir. */}
      {(orphelines.donnees?.length ?? 0) > 0 && (
        <div className="carte mb-4 border-amber-200 bg-amber-50 p-4">
          <h2 className="text-sm font-semibold text-amber-900">
            {orphelines.donnees!.length} activité(s) sans type, donc sans tarif de référence
          </h2>
          <p className="mt-1 text-sm text-amber-800">
            {orphelines.donnees!.map((a) => a.libelle).join(', ')}
          </p>
          <p className="mt-1 text-xs text-amber-700">
            Rattachez-les depuis Référentiels → Activités.
          </p>
        </div>
      )}

      <label className="mb-4 inline-flex items-center gap-2 text-sm text-encre-600">
        <input
          type="checkbox"
          checked={inclureInactifs}
          onChange={(evenement) => setInclureInactifs(evenement.target.checked)}
        />
        Afficher les types désactivés
      </label>

      {types.erreur && <Erreur message={types.erreur} onReessayer={types.recharger} />}
      {types.chargement ? (
        <Chargement />
      ) : (
        <div className="carte">
          <Tableau
            colonnes={colonnes}
            lignes={lignes}
            cleDe={(t) => t.id}
            messageVide="Aucun type d’activité."
            onLigneCliquee={peutGerer ? (t) => setModale({ ouverte: true, type: t }) : undefined}
            onReordonner={peutGerer ? (ids) => void reordonner(ids) : undefined}
          />
        </div>
      )}

      <FormulaireType
        etat={modale}
        anneeId={annee.anneeId}
        onFermer={() => setModale({ ouverte: false, type: null })}
        onEnregistre={() => {
          setModale({ ouverte: false, type: null });
          types.recharger();
          orphelines.recharger();
        }}
      />
    </>
  );
}

function FormulaireType({
  etat,
  anneeId,
  onFermer,
  onEnregistre,
}: {
  etat: { ouverte: boolean; type: TypeActivite | null };
  anneeId: string;
  onFermer: () => void;
  onEnregistre: () => void;
}) {
  const [valeurs, setValeurs] = useState({
    code: '',
    libelle: '',
    famille: 'Instruments' as FamilleActivite,
    tarif: '',
    dureeMinutes: 60,
    capaciteMax: '',
    tolerancePourcent: '0',
    optionForfaitEssai: false,
    tarifEssai: '',
    // `null` : pas encore choisi — la nature suit alors la capacite
    // (1 place = individuel), comme le serveur le fait a la creation.
    individuel: null as boolean | null,
    suiviPresence: 'aucun' as SuiviPresence,
    commentaire: '',
  });
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);
  const [initialisePour, setInitialisePour] = useState<string | null>(null);

  const cle = etat.ouverte ? (etat.type?.id ?? 'nouveau') : null;
  if (cle && cle !== initialisePour) {
    setInitialisePour(cle);
    setValeurs({
      code: etat.type?.code ?? '',
      libelle: etat.type?.libelle ?? '',
      famille: etat.type?.famille ?? 'Instruments',
      tarif: etat.type ? enEuros(etat.type.tarif) : '',
      dureeMinutes: etat.type?.duree_minutes ?? 60,
      capaciteMax: String(etat.type?.capacite_max ?? ''),
      tolerancePourcent: String(etat.type?.tolerance_pourcent ?? 0),
      optionForfaitEssai: etat.type?.option_forfait_essai ?? false,
      tarifEssai: etat.type?.tarif_essai != null ? enEuros(etat.type.tarif_essai) : '',
      individuel: etat.type?.individuel ?? null,
      suiviPresence: etat.type?.suivi_presence ?? 'aucun',
      commentaire: etat.type?.commentaire ?? '',
    });
    setErreur(null);
  }
  if (!etat.ouverte && initialisePour !== null) setInitialisePour(null);

  // La nature affichee : le choix pose, a defaut ce que dit la capacite.
  const individuel = valeurs.individuel ?? valeurs.capaciteMax === '1';

  const soumettre = async (evenement: FormEvent) => {
    evenement.preventDefault();
    setErreur(null);

    const tarif = enCentimes(valeurs.tarif);
    if (tarif === null) {
      setErreur('Tarif invalide : utilisez le format 950,00.');
      return;
    }

    setEnCours(true);
    try {
      const corps = {
        libelle: valeurs.libelle,
        famille: valeurs.famille,
        tarif,
        dureeMinutes: valeurs.dureeMinutes,
        // `null` explicite : vide = pas de limite definie.
        capaciteMax: valeurs.capaciteMax ? Number(valeurs.capaciteMax) : null,
        tolerancePourcent: Number(valeurs.tolerancePourcent) || 0,
        optionForfaitEssai: valeurs.optionForfaitEssai,
        // Le montant du forfait ne vaut qu'avec l'option ; vide, il se retire.
        tarifEssai: valeurs.optionForfaitEssai && valeurs.tarifEssai ? enCentimes(valeurs.tarifEssai) : null,
        individuel,
        suiviPresence: valeurs.suiviPresence,
        commentaire: valeurs.commentaire || null,
      };
      if (etat.type) {
        await api.patch(`/api/types-activite/${etat.type.id}`, corps);
      } else {
        // Le type est cree sur l'ANNEE affichee par le selecteur.
        await api.post('/api/types-activite', { ...corps, code: valeurs.code, anneeId });
      }
      onEnregistre();
    } catch (cause) {
      setErreur(cause instanceof ErreurApi ? cause.message : 'Enregistrement impossible.');
    } finally {
      setEnCours(false);
    }
  };

  return (
    <Modale
      titre={etat.type ? `Modifier ${etat.type.libelle}` : 'Nouveau type d’activité'}
      ouverte={etat.ouverte}
      onFermer={onFermer}
    >
      <form onSubmit={soumettre} className="space-y-4">
        {!etat.type && (
          <Champ
            libelle="Code"
            aide="Référence stable, utilisée par les scripts de reprise. Il ne pourra plus être modifié."
          >
            <input
              className="champ font-mono uppercase"
              value={valeurs.code}
              onChange={(e) => setValeurs({ ...valeurs, code: e.target.value.toUpperCase() })}
              required
              autoFocus
            />
          </Champ>
        )}

        <Champ libelle="Libellé">
          <input
            className="champ"
            value={valeurs.libelle}
            onChange={(e) => setValeurs({ ...valeurs, libelle: e.target.value })}
            required
          />
        </Champ>

        <Champ
          libelle="Famille"
          aide="Une des cinq catégories du cahier des charges. C’est elle que ventile le tableau de bord — la changer déplace les activités rattachées."
        >
          <select
            className="champ"
            value={valeurs.famille}
            onChange={(e) => setValeurs({ ...valeurs, famille: e.target.value as FamilleActivite })}
          >
            {FAMILLES.map((famille) => (
              <option key={famille} value={famille}>
                {famille}
              </option>
            ))}
          </select>
        </Champ>

        <Champ
          libelle="Tarif annuel (€)"
          aide={
            (etat.type?.nb_inscriptions ?? 0) > 0
              ? `Verrouillé : ${etat.type!.nb_inscriptions} inscription(s) ont déjà été saisies sur les activités de ce type cette année. Les tarifs se révisent à la création de l'année suivante.`
              : 'Pré-remplit la saisie d’inscription. Modifiable tant qu’aucune inscription n’a été saisie sur ce type cette année.'
          }
        >
          <input
            className={`champ tabulaire ${
              (etat.type?.nb_inscriptions ?? 0) > 0 ? 'bg-encre-50 text-encre-600' : ''
            }`}
            value={valeurs.tarif}
            onChange={(e) => setValeurs({ ...valeurs, tarif: e.target.value })}
            inputMode="decimal"
            required
            readOnly={(etat.type?.nb_inscriptions ?? 0) > 0}
          />
        </Champ>

        <Champ
          libelle="Durée d’une séance (minutes)"
          aide="Sert de valeur par défaut aux activités de ce type, et calcule l’heure de fin d’un créneau depuis son heure de début."
        >
          <input
            className="champ tabulaire"
            type="number"
            min={5}
            max={480}
            step={5}
            value={valeurs.dureeMinutes}
            onChange={(e) => setValeurs({ ...valeurs, dureeMinutes: Number(e.target.value) || 0 })}
            required
          />
        </Champ>

        <div className="grid grid-cols-2 gap-3">
          <Champ
            libelle="Capacité maximale (places)"
            aide="Nombre d’élèves qu’un cours de ce type accueille. Elle descend sur les activités du type, puis sur leurs créneaux — chacun pouvant l’abaisser. Vide : pas de limite."
          >
            <input
              className="champ tabulaire"
              type="number"
              min={1}
              max={999}
              value={valeurs.capaciteMax}
              onChange={(e) => setValeurs({ ...valeurs, capaciteMax: e.target.value })}
            />
          </Champ>
          <Champ
            libelle="Tolérance (%)"
            aide={(() => {
              const capacite = Number(valeurs.capaciteMax);
              const tolerance = Number(valeurs.tolerancePourcent) || 0;
              const plafond =
                capacite > 0 ? Math.ceil(capacite * (1 + tolerance / 100)) : null;
              return `L’inscription reste possible au-delà de la capacité, jusqu’à capacité + tolérance, arrondie à l’unité supérieure${
                plafond !== null ? ` — soit ${plafond} places ici` : ''
              }. 0 : capacité stricte.`;
            })()}
          >
            <input
              className="champ tabulaire"
              type="number"
              min={0}
              max={100}
              value={valeurs.tolerancePourcent}
              onChange={(e) => setValeurs({ ...valeurs, tolerancePourcent: e.target.value })}
            />
          </Champ>
        </div>

        <Champ
          libelle="Nature du cours"
          aide="Un cours individuel le reste quand deux élèves alternent sur un créneau de deux places : la capacité ne décide pas."
        >
          <select
            className="champ"
            value={individuel ? 'individuel' : 'groupe'}
            onChange={(e) => setValeurs({ ...valeurs, individuel: e.target.value === 'individuel' })}
          >
            <option value="groupe">Cours de groupe</option>
            <option value="individuel">Cours individuel</option>
          </select>
        </Champ>

        {/* Suivi des presences (2026-10-13, lot 2) : ce que le type attend
            des seances. Non applicable par defaut. */}
        <Champ libelle="Suivi des présences">
          <select
            className="champ"
            value={valeurs.suiviPresence}
            onChange={(e) =>
              setValeurs({ ...valeurs, suiviPresence: e.target.value as SuiviPresence })
            }
          >
            <option value="aucun">Non applicable</option>
            <option value="presence">Présence</option>
            <option value="absence">Absence seulement</option>
          </select>
        </Champ>

        <label className="flex items-start gap-2 text-sm">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={valeurs.optionForfaitEssai}
            onChange={(e) => setValeurs({ ...valeurs, optionForfaitEssai: e.target.checked })}
          />
          <span>
            Option Forfait d’Essai
            <span className="mt-0.5 block text-xs text-encre-400">
              Les activités de ce type proposeront à l’inscription un essai de 3 séances avant
              l’engagement. La date de fin d’essai se calcule sur le calendrier des séances.
            </span>
          </span>
        </label>
        {valeurs.optionForfaitEssai && (
          <Champ
            libelle="Montant du forfait d’essai (€)"
            aide="Ce que l’élève doit tant que l’essai n’est pas confirmé. Copié sur l’inscription à la saisie."
          >
            <input
              className="champ tabulaire"
              value={valeurs.tarifEssai}
              onChange={(e) => setValeurs({ ...valeurs, tarifEssai: e.target.value })}
              inputMode="decimal"
              placeholder="100,00"
            />
          </Champ>
        )}

        <Champ libelle="Conditions" aide="Effectif, conditions d’accès — repris sur les documents.">
          <input
            className="champ"
            value={valeurs.commentaire}
            onChange={(e) => setValeurs({ ...valeurs, commentaire: e.target.value })}
          />
        </Champ>

        <PiedFormulaire erreur={erreur} enCours={enCours} onFermer={onFermer} />
      </form>
    </Modale>
  );
}
